import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withTiming,
  withRepeat,
  Easing,
  cancelAnimation,
} from 'react-native-reanimated';
import { ViewStyle, StyleProp } from 'react-native';
import styled from '@emotion/native';
import { green, white } from '@/shared/styles/colors';
import { BaseText } from '../StyledText';

interface PillTickerProps {
  tags: string[];
  maxWidth?: number;
  containerStyle?: StyleProp<ViewStyle>;
}

interface PillItemProps {
  text: string;
  itemOriginalIndex?: number;
  onItemLayout?: (originalIndex: number, width: number) => void;
}

const SPEED = 25; // pixels per second
const PILL_MARGIN = 6;

const PillItem: React.FC<PillItemProps> = ({
  text,
  itemOriginalIndex,
  onItemLayout,
}) => {
  return (
    <PillBase
      onLayout={(event) => {
        // Only report layout if from the original set of tags, which will have a defined itemOriginalIndex
        if (onItemLayout && typeof itemOriginalIndex === 'number') {
          onItemLayout(itemOriginalIndex, event.nativeEvent.layout.width);
        }
      }}
    >
      <PillText>{text}</PillText>
    </PillBase>
  );
};

export function PillTicker({
  tags,
  maxWidth,
  containerStyle,
}: PillTickerProps): React.ReactElement | null {
  const [measuredContentWidth, setMeasuredContentWidth] = useState<number>(0);
  const [individualPillWidths, setIndividualPillWidths] = useState<
    Record<number, number>
  >({});
  const [tickerContainerWidth, setTickerContainerWidth] = useState<number>(0);
  const translateX = useSharedValue(0);

  const shouldAnimate = useMemo(() => {
    if (
      tags.length === 0 ||
      measuredContentWidth === 0 ||
      tickerContainerWidth === 0
    ) {
      return false;
    }
    // Animate only if the content width is greater than the container width
    // Adding a small tolerance (e.g., 1px) can prevent animation for negligible overflows.
    return measuredContentWidth > tickerContainerWidth + 1;
  }, [measuredContentWidth, tickerContainerWidth, tags.length]);

  // Prepare tags for display: duplicate for animation, single set otherwise
  const displayedTags = useMemo(() => {
    if (shouldAnimate && tags.length > 0) {
      return [...tags, ...tags];
    }
    return tags;
  }, [tags, shouldAnimate]);

  const animatedStyle = useAnimatedStyle(() => {
    return {
      transform: [{ translateX: translateX.value }],
    };
  });

  // Callback to handle individual pill layout
  const handlePillLayout = useCallback(
    (originalIndex: number, width: number) => {
      setIndividualPillWidths((prevWidths) => {
        if (width > 0 && prevWidths[originalIndex] !== width) {
          return { ...prevWidths, [originalIndex]: width };
        }
        return prevWidths;
      });
    },
    [],
  );

  useEffect(() => {
    setIndividualPillWidths({});
  }, [tags]);

  // Effect to calculate total width from individual pills
  useEffect(() => {
    if (
      tags.length > 0 &&
      Object.keys(individualPillWidths).length === tags.length
    ) {
      const allWidthsReported = tags.every(
        (_, index) =>
          typeof individualPillWidths[index] === 'number' &&
          individualPillWidths[index] > 0,
      );

      if (allWidthsReported) {
        const sumOfIndividualWidths = Object.values(
          individualPillWidths,
        ).reduce((sum, width) => sum + width, 0);
        const totalHorizontalMarginSpace = tags.length * (PILL_MARGIN * 2);
        const newCalculatedWidth =
          sumOfIndividualWidths + totalHorizontalMarginSpace;

        if (newCalculatedWidth > 0) {
          const MIN_DIFFERENCE_THRESHOLD = 0.5;
          if (
            measuredContentWidth === 0 ||
            Math.abs(newCalculatedWidth - measuredContentWidth) >
              MIN_DIFFERENCE_THRESHOLD
          ) {
            setMeasuredContentWidth(newCalculatedWidth);
          }
        }
      }
    } else if (tags.length === 0 && measuredContentWidth !== 0) {
      setMeasuredContentWidth(0);
    }
  }, [individualPillWidths, tags, measuredContentWidth]);

  useEffect(() => {
    if (!shouldAnimate) {
      cancelAnimation(translateX);
      // Smoothly reset to 0, or just set to 0 if no animation was in progress.
      // Using withTiming for a smoother transition to the centered state.
      translateX.value = withTiming(0, {
        duration: 150,
        easing: Easing.out(Easing.quad),
      });
      return;
    }

    // Reset position to 0 before starting the loop animation.
    translateX.value = 0;

    const animationDuration = (measuredContentWidth / SPEED) * 1000;

    translateX.value = withRepeat(
      withTiming(-measuredContentWidth, {
        // Animate to the end of the first set of tags
        duration: animationDuration,
        easing: Easing.linear,
      }),
      -1, // Infinite loop
      false, // Do not reverse, just restart
    );

    return () => {
      cancelAnimation(translateX);
    };
  }, [measuredContentWidth, shouldAnimate, tags.length, translateX]);

  if (!tags || tags.length === 0) {
    return null; // Or render a placeholder
  }

  return (
    <TickerContainer
      onLayout={(event) => {
        const { width } = event.nativeEvent.layout;
        // Set width only if it's positive and different from current to avoid redundant re-renders
        if (width > 0 && tickerContainerWidth !== width) {
          setTickerContainerWidth(width);
        }
      }}
      style={[maxWidth ? { maxWidth: maxWidth } : {}, containerStyle]}
    >
      <PillsWrapper style={animatedStyle}>
        {displayedTags.map((tag, index) => {
          const originalTagIndex = index % tags.length;
          const isOriginalTagForMeasurement = index < tags.length;

          return (
            <PillItem
              key={`pill-${tag}-${index}`} // Ensures unique keys even with duplicated tags
              text={tag}
              itemOriginalIndex={
                isOriginalTagForMeasurement ? originalTagIndex : undefined
              }
              onItemLayout={
                isOriginalTagForMeasurement ? handlePillLayout : undefined
              }
            />
          );
        })}
      </PillsWrapper>
    </TickerContainer>
  );
}

const TickerContainer = styled.View({
  overflow: 'hidden',
  height: 50,
  justifyContent: 'center',
});

const PillsWrapper = styled(Animated.View)({
  flexDirection: 'row',
});

const PillBase = styled.View({
  backgroundColor: green,
  paddingVertical: 10,
  paddingHorizontal: 18,
  borderRadius: 26,
  marginVertical: 0,
  marginHorizontal: PILL_MARGIN,
  alignSelf: 'flex-start',
});

const PillText = styled(BaseText)({
  color: white,
  fontSize: 14,
  fontWeight: 'bold',
});

export default PillTicker;
